// BOM Preview Page (FR-1.5, FR-1.6)
// Preview parsed data with column mapping editor, confidence badges, and BOM creation
import React, { useState, useEffect, useCallback, useRef } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import ProcessingChoice, { PROCESSING_LEVELS, processingLevel, useProcessingChoice } from '../../components/bom-import/ProcessingChoice';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  Stack,
  Tooltip,
  IconButton,
  TextField,
  Checkbox,
  FormControlLabel,
  Popover,
  Collapse,
  alpha,
} from '@mui/material';
import {
  CheckCircle as ValidIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Visibility as PreviewIcon,
  AttachFile as AttachFileIcon,
  Description as DescriptionIcon,
  Build as BuildIcon,
  TravelExplore as DiscoveryIcon,
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { bomIntelligenceService } from '../../services/bomIntelligenceService';
import { semanticColors } from '../../theme/theme';
import { ThemeProvider } from '@mui/material/styles';
import { IBM_PLEX_SANS, useIbmPlexFont, withIbmPlex } from '../../theme/ibmPlex';
import type {
  AnalysisPreview,
  ColumnMapping,
  LineGoals,
  ParsedRow,
  PartClassificationCode,
  SourcingModeCode,
  PendingDocument,
} from '../../types';
import CadPreviewPage from './CadPreviewPage';
import { useAppDispatch, setBreadcrumbItems } from '@ciea.ai/smart-infra-navigation';
import BomPreviewDataGrid from './BomPreviewDataGrid';
import { SegmentedChoice } from '../../components/SegmentedChoice';
import type { ImportSetup } from '../CreateBomPage';
import { T } from '../../components/portfolio/PortfolioParts';

import {
  SourcingPolicyControl,
  type AlternativesSearchChoice,
  type ComplianceCheckChoice,
} from '../../components/sourcing/SourcingPolicyControl';
import { COMPLIANCE_REGULATION_OPTIONS, DEFAULT_COMPLIANCE_REGULATIONS } from '../../components/sourcing/ComplianceCheckControl';
import externalDatabasesService, { type OfferSearchMode } from '../../services/externalDatabasesService';
import {
  SourcingConstraints,
  ConstraintMode,
  EMPTY_CONSTRAINTS,
  cleanConstraints,
  effectiveForLine,
} from '../../types/sourcingConstraints';

// Slice -1: part classification options shown in the Type-column dropdown.
// Default = STANDARD_CATALOG; auto-classifier writes its result here if the
// row is unset and confidence ≥ 0.6. Drawing upload forces CUSTOM_MACHINED.
const PART_CLASSIFICATION_OPTIONS: Array<{
  value: PartClassificationCode;
  label: string;
  short: string;
  color: string;
}> = [
  { value: 'STANDARD_CATALOG', label: 'Standard / Catalog', short: 'STD', color: '#64748b' },
  { value: 'CUSTOM_MACHINED', label: 'Custom (machined / mfg)', short: 'CUSTOM', color: '#d97706' },
  { value: 'ASSEMBLY', label: 'Assembly (has children)', short: 'ASSY', color: '#7c3aed' },
  { value: 'FASTENER', label: 'Fastener', short: 'FAST', color: '#0891b2' },
  { value: 'RAW_MATERIAL', label: 'Raw material', short: 'RAW', color: '#65a30d' },
];

// Filename normalizer for bulk-drop auto-match. Strips ext, lowercases,
// removes non-alphanumerics so "BRACKET-001 Rev A.pdf" matches "bracket001".
const normalizeForMatch = (s: string): string =>
  s
    .replace(/\.[^.]+$/, '')
    .toLowerCase()
    .replace(/\brev\s*[a-z0-9]+\b/g, '')
    .replace(/[^a-z0-9]/g, '');

// Target BOM fields available for mapping
const TARGET_FIELDS = [
  { value: '', label: '-- Skip --' },
  { value: 'partNumber', label: 'Part Number' },
  { value: 'mpn', label: 'MPN (Manufacturer Part Number)' },
  { value: 'partDescription', label: 'Description' },
  { value: 'quantity', label: 'Quantity' },
  { value: 'unitOfMeasure', label: 'Unit of Measure' },
  { value: 'unitCost', label: 'Unit Cost' },
  // Sourcing goals per line (optional).
  { value: 'requiredQuantity', label: 'Required Quantity (to buy)' },
  { value: 'targetPrice', label: 'Target Price' },
  { value: 'targetLeadTime', label: 'Target Lead Time' },
  { value: 'manufacturerName', label: 'Manufacturer' },
  { value: 'manufacturerId', label: 'Manufacturer ID' },
  // Hierarchy / structural fields — multi-level BOMs (FG → Assembly → Sub-Assy → Component).
  // `itemNumber` is the WBS-style "1.1.1.2" position; the importer parses it to derive
  // parentLineItemId. `level` is an alternate signal (stack-based parent inference).
  // `type` (Assembly / Sub-Assy / Component / FG) flips line items between PART and ASSEMBLY.
  { value: 'itemNumber', label: 'Item No. (Hierarchy "1.1.1.2")' },
  { value: 'level', label: 'Level / Indent' },
  { value: 'lineNumber', label: 'Line Number' },
  { value: 'type', label: 'Type (Assembly / Component)' },
  { value: 'referenceDesignator', label: 'Reference Designator' },
  { value: 'findNumber', label: 'Find Number' },
  { value: 'materialType', label: 'Category / Material Type' },
  { value: 'notes', label: 'Notes' },
  { value: 'isCritical', label: 'Critical Flag' },
  { value: 'centralItemCode', label: 'Central Item Code' },
  { value: 'centralItemDesc', label: 'Central Item Desc' },
  { value: 'companyCode', label: 'Company Code' },
  { value: 'companyDesc', label: 'Company Name' },
  { value: 'supplierCode', label: 'Supplier Code' },
  { value: 'supplierDesc', label: 'Supplier Name' },
  { value: 'supplierCurrency', label: 'Supplier Currency' },
  { value: 'casNumber', label: 'CAS Number' },
];

// ────────────── Slice -1: per-row generic document attach ──────────────
// One icon per row. Click → 2-button popover (Technical Drawing | Datasheet).
// Drag-drop a file onto the icon → infer type from filename (skip popover).
// Multiple documents per row supported — extra attachments stack as small pills
// next to the icon. Color signals type: amber=DRAWING (CUSTOM), blue=DATASHEET,
// gray=other. The act of attaching a DRAWING also flips the row to CUSTOM_MACHINED
// server-side; nothing else changes classification.

type DocType = 'DRAWING' | 'DATASHEET' | 'REQUIREMENTS' | 'CERT' | 'MSDS' | 'OTHER';

const DOC_TYPE_VISUALS: Record<DocType, { icon: SvgIconComponent; color: string; label: string; tagline: string }> = {
  DRAWING: {
    icon: BuildIcon,
    color: '#d97706', // amber — matches CUSTOM badge
    label: 'Technical Drawing',
    tagline: 'Marks row as CUSTOM_MACHINED',
  },
  DATASHEET: {
    icon: DescriptionIcon,
    color: '#0284c7', // blue
    label: 'Datasheet',
    tagline: 'Catalog / spec reference',
  },
  REQUIREMENTS: {
    icon: DescriptionIcon,
    color: '#0d9488', // teal
    label: 'Requirements',
    tagline: 'What the customer asks for',
  },
  CERT: { icon: DescriptionIcon, color: '#7c3aed', label: 'Certificate', tagline: '' },
  MSDS: { icon: DescriptionIcon, color: '#0891b2', label: 'MSDS', tagline: '' },
  OTHER: { icon: AttachFileIcon, color: '#64748b', label: 'Other', tagline: '' },
};

/** Infer doc type from filename — used by the drag-drop fast path. */
function inferDocType(fileName: string): DocType | null {
  const lower = fileName.toLowerCase();
  if (/(?:^|[^a-z])(?:rf[qpi]|req(?:uest|uirements?)?\d*|tender|sow|enquiry|inquiry)(?:[^a-z]|$)/.test(lower)) return 'REQUIREMENTS';
  if (/drawing|dwg|drw|\bgd&?t\b/.test(lower)) return 'DRAWING';
  if (/datasheet|data[- _]?sheet|\bds[\-_.]|spec(ification)?/.test(lower)) return 'DATASHEET';
  if (/msds|sds[\-_.]/.test(lower)) return 'MSDS';
  if (/cert|coa|conformance/.test(lower)) return 'CERT';
  return null;
}

interface DocumentChipProps {
  documents: PendingDocument[];
  busy: boolean;
  onUpload: (file: File, type: DocType) => Promise<void> | void;
  onDetach: (documentId: string) => Promise<void> | void;
  /** What was read from the row's requirements document, shown on its tag. */
  requirements?: ParsedRow['requirements'];
}

const DocumentChip: React.FC<DocumentChipProps> = ({ documents, busy, onUpload, onDetach, requirements }) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const docs = documents || [];
  const hasDrawing = docs.some((d) => d.documentType === 'DRAWING');
  const hasDatasheet = docs.some((d) => d.documentType === 'DATASHEET');

  // Anchor color signals what's attached. Multi-type rows get the "stronger"
  // color (drawing > datasheet > nothing).
  const accentColor = hasDrawing
    ? DOC_TYPE_VISUALS.DRAWING.color
    : hasDatasheet
      ? DOC_TYPE_VISUALS.DATASHEET.color
      : '#94a3b8';

  const openPopoverWithFile = (file: File, anchor: HTMLElement) => {
    const inferred = inferDocType(file.name);
    if (inferred === 'DRAWING' || inferred === 'DATASHEET' || inferred === 'REQUIREMENTS') {
      // Fast path: filename is unambiguous → skip popover.
      onUpload(file, inferred);
      return;
    }
    setPendingFile(file);
    setPopoverAnchor(anchor);
  };

  const openPickerExpectType = () => {
    setPendingFile(null);
    inputRef.current?.click();
  };

  const handleTypeSelected = (type: DocType) => {
    if (pendingFile) {
      onUpload(pendingFile, type);
      setPendingFile(null);
      setPopoverAnchor(null);
    } else {
      // Pre-selected type, picker not yet opened
      setPendingFile(null);
      const ref = inputRef.current;
      if (!ref) return;
      // Stash the chosen type via a data attribute the change handler reads
      ref.setAttribute('data-pending-type', type);
      setPopoverAnchor(null);
      ref.click();
    }
  };

  return (
    <Box
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!dragOver) setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) openPopoverWithFile(f, e.currentTarget as HTMLElement);
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        p: 0.25,
        borderRadius: 1,
        ...(dragOver
          ? {
              outline: `2px dashed ${semanticColors.accent.main}`,
              outlineOffset: 1,
              bgcolor: alpha(semanticColors.accent.main, 0.05),
            }
          : {}),
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) {
            const pendingType = (e.target.getAttribute('data-pending-type') as DocType | null) || null;
            e.target.removeAttribute('data-pending-type');
            if (pendingType) {
              onUpload(f, pendingType);
            } else {
              openPopoverWithFile(f, e.target as unknown as HTMLElement);
            }
          }
          e.target.value = '';
        }}
      />

      {/* Main attach icon — always visible. Click opens type picker; drag-drop infers. */}
      <Tooltip title={docs.length > 0 ? `${docs.length} attached — drag a file to add another` : 'Attach drawing or datasheet'}>
        <IconButton
          size="small"
          onClick={(e) => {
            // If nothing attached yet, open the popover so the user picks
            // a type FIRST (better mental model — explicit). If something
            // already attached, open the file picker directly and we'll
            // ask the type after the file lands.
            if (docs.length === 0) {
              setPendingFile(null);
              setPopoverAnchor(e.currentTarget);
            } else {
              openPickerExpectType();
            }
          }}
          disabled={busy}
          sx={{
            color: accentColor,
            border: docs.length > 0 ? `1.5px solid ${accentColor}` : '1.5px dashed #cbd5e1',
            bgcolor: docs.length > 0 ? alpha(accentColor, 0.08) : 'transparent',
            width: 28,
            height: 28,
            '&:hover': { bgcolor: alpha(accentColor, 0.16), borderColor: accentColor },
          }}
        >
          {busy ? <CircularProgress size={14} /> : <AttachFileIcon sx={{ fontSize: 16 }} />}
        </IconButton>
      </Tooltip>

      {/* Attached docs — small pills, one per attachment. Click X to detach. */}
      {docs.map((d) => {
        const visual = DOC_TYPE_VISUALS[d.documentType] || DOC_TYPE_VISUALS.OTHER;
        const VisualIcon = visual.icon;
        return (
          <Tooltip
            key={d.documentId}
            title={d.documentType === 'REQUIREMENTS' && requirements ? (
              <Box sx={{ fontSize: '0.72rem', maxWidth: 420 }}>
                <b>Requirements: {d.fileName}</b>
                <Box sx={{ mt: 0.5 }}>{requirements.summary}</Box>
                {(requirements.technical ?? []).filter((t) => t.strictness === 'required').slice(0, 12).map((t, i) => (
                  <Box key={i} sx={{ mt: 0.25 }}>{t.name}: {t.value}</Box>
                ))}
                {(requirements.technical ?? []).filter((t) => t.strictness === 'required').length > 12 && <Box sx={{ mt: 0.25 }}>and more</Box>}
              </Box>
            ) : `${visual.label}: ${d.fileName}${d.revision ? ` (rev ${d.revision})` : ''}${d.extractionStatus ? ` · ${d.extractionStatus}` : ''}`}
          >
            <Chip
              icon={<VisualIcon sx={{ fontSize: 12, color: visual.color }} />}
              label={d.documentType === 'DRAWING' ? 'DWG' : d.documentType === 'DATASHEET' ? 'DS' : d.documentType === 'REQUIREMENTS' ? 'REQ' : d.documentType.slice(0, 3)}
              size="small"
              onDelete={busy ? undefined : () => onDetach(d.documentId)}
              deleteIcon={<CloseIcon sx={{ fontSize: 12 }} />}
              sx={{
                fontSize: '0.6rem',
                height: 18,
                fontWeight: 700,
                borderColor: visual.color,
                color: visual.color,
                bgcolor: alpha(visual.color, 0.1),
                '& .MuiChip-icon': { ml: 0.5 },
                '& .MuiChip-deleteIcon': { color: visual.color, '&:hover': { color: '#475569' } },
              }}
              variant="outlined"
            />
          </Tooltip>
        );
      })}

      {/* Two-button popover — open when user clicks the icon and there are no docs yet,
          or when drag-drop dropped an ambiguous filename and we need to ask. */}
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={() => {
          setPopoverAnchor(null);
          setPendingFile(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, p: 1.5 } } }}
      >
        <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#64748b', fontWeight: 600 }}>
          {pendingFile ? `What kind of document is "${pendingFile.name}"?` : 'What kind of document?'}
        </Typography>
        <Stack direction="row" spacing={1.5}>
          {(['DRAWING', 'DATASHEET', 'REQUIREMENTS'] as DocType[]).map((type) => {
            const v = DOC_TYPE_VISUALS[type];
            const VIcon = v.icon;
            return (
              <Box
                key={type}
                onClick={() => handleTypeSelected(type)}
                sx={{
                  cursor: 'pointer',
                  width: 130,
                  p: 1.5,
                  border: `1.5px solid ${alpha(v.color, 0.3)}`,
                  borderRadius: 2,
                  textAlign: 'center',
                  transition: 'all 0.12s ease',
                  '&:hover': {
                    borderColor: v.color,
                    bgcolor: alpha(v.color, 0.08),
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                <VIcon sx={{ fontSize: 28, color: v.color, mb: 0.5 }} />
                <Typography variant="body2" sx={{ fontWeight: 700, color: v.color }}>
                  {v.label}
                </Typography>
                {v.tagline && (
                  <Typography variant="caption" sx={{ display: 'block', color: '#64748b', mt: 0.25, fontSize: '0.65rem' }}>
                    {v.tagline}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      </Popover>
    </Box>
  );
};

const BomPreviewPage: React.FC = () => {
  useIbmPlexFont();
  const { analysisId } = useParams<{ analysisId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(setBreadcrumbItems([
      { label: 'Bill of Materials', path: '/bom' },
      { label: 'Preview', path: `/bom-intelligence/preview/${analysisId}` },
    ]));
  }, [dispatch, analysisId]);

  const [preview, setPreview] = useState<AnalysisPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [columnMapping, setColumnMapping] = useState<ColumnMapping[]>([]);
  const [excludedRows, setExcludedRows] = useState<Set<number>>(new Set());
  const [bomName, setBomName] = useState('');
  const [workflowId] = useState<string>(searchParams.get('workflowId') || 'spec-upload');
  const [creating, setCreating] = useState(false);
  const [partPool, setPartPool] = useState<'gold' | 'central' | 'local'>('local');
  const [concurrency, setConcurrency] = useState<number>(10);
  const [processing, setProcessing] = useProcessingChoice();
  // Opened from Create BOM with "Review lines first": the settings chosen there apply here.
  const setupFromCreate = (useLocation().state as { setup?: ImportSetup } | null)?.setup;
  const appliedSetup = useRef(false);
  const [pnPreview, setPnPreview] = useState<{
    enabled: boolean;
    prefix: string | null;
    samples: string[];
    nextValue: number;
    sequencePadding: number;
    pattern: string;
  } | null>(null);
  const [pnPrefixOverride, setPnPrefixOverride] = useState<string>('');
  const [forceAutoGenPn, setForceAutoGenPn] = useState<boolean>(false);
  // The BOM's run mode. ON (Reuse): existing parts, known sellers and known alternatives are used.
  // OFF (Fresh): nothing from earlier runs is used, for the whole run.
  const [reuseExistingParts, setReuseExistingParts] = useState<boolean>(true);
  // Buyer sourcing constraints declared at import for the whole package — region
  // + required certs are HARD filters in every sourcing search for its parts;
  // free-text rules are honored by the agents. Persisted on Bom.sourcingConstraints.
  const [sourcingPolicy, setSourcingPolicy] = useState<SourcingConstraints>(EMPTY_CONSTRAINTS);
  // Whether alternatives are searched for this BOM. null = not chosen: nothing
  // is sent and the flow decides, as before this choice existed.
  const [bomAlternatives, setBomAlternatives] = useState<AlternativesSearchChoice>(null);
  // Whether the compliance check runs for this BOM, and for which regulations.
  // null = OFF; empty regulations = the defaults.
  const [bomCompliance, setBomCompliance] = useState<ComplianceCheckChoice>(null);
  const [bomRegulations, setBomRegulations] = useState<string[]>([]);
  // Where this BOM's lines look for offers (null follows the company); set on the BOM once it is created.
  const [bomOfferMode, setBomOfferMode] = useState<OfferSearchMode | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  useEffect(() => {
    if (!setupFromCreate || appliedSetup.current) return;
    appliedSetup.current = true;
    setSourcingPolicy(setupFromCreate.sourcingPolicy);
    setBomAlternatives(setupFromCreate.alternativesSearch);
    setBomCompliance(setupFromCreate.complianceCheck ?? null);
    setBomRegulations(setupFromCreate.complianceRegulations ?? []);
    setProcessing(setupFromCreate.processing);
    setReuseExistingParts(setupFromCreate.reuseExistingParts);
    setPartPool(setupFromCreate.partPool);
    setConcurrency(setupFromCreate.concurrency);
    setForceAutoGenPn(setupFromCreate.generatePartNumbers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupFromCreate]);

  // Per-row in-flight indicator (document upload / classification / sourcing).
  // Keyed by lineItemId. Used to disable the relevant control while a request
  // is in flight without blocking the rest of the row.
  const [busyRows, setBusyRows] = useState<Set<string>>(new Set());

  const loadPreview = useCallback(async () => {
    if (!analysisId) return;

    try {
      setLoading(true);
      const data = await bomIntelligenceService.getPreview(analysisId);
      setPreview(data);
      setColumnMapping(data.columnMapping || []);
      // Seed BOM name from the analysis job (captured on upload) so the user
      // never has to re-type what they already entered. Fall back to filename,
      // except for a pasted list: it has no name of its own, so the person names it.
      const persistedName = (data as any).bomName
        || (data.importMode === 'pasted-text' ? '' : (data.originalFilename || '').replace(/\.[^.]+$/, ''));
      if (persistedName) setBomName((current) => current || persistedName);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, [analysisId]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  // Refresh part-numbering preview whenever the user toggles partPool or
  // edits the prefix override. Cheap GET; debounce isn't worth it here.
  useEffect(() => {
    if (!analysisId) return;
    let cancelled = false;
    bomIntelligenceService
      .getPartNumberingPreview(analysisId, {
        partPool,
        prefixOverride: pnPrefixOverride.trim() || undefined,
      })
      .then((data) => { if (!cancelled) setPnPreview(data); })
      .catch(() => { if (!cancelled) setPnPreview(null); });
    return () => { cancelled = true; };
  }, [analysisId, partPool, pnPrefixOverride]);

  const handleMappingChange = (sourceColumn: string, newTargetField: string) => {
    setColumnMapping(prev =>
      prev.map(m =>
        m.sourceColumn === sourceColumn
          ? { ...m, targetField: newTargetField, isManualOverride: true, confidence: 1.0 }
          : m,
      ),
    );
  };

  const handleConcatenateToggle = (sourceColumn: string) => {
    setColumnMapping(prev =>
      prev.map(m =>
        m.sourceColumn === sourceColumn
          ? { ...m, concatenate: !m.concatenate, isManualOverride: true }
          : m,
      ),
    );
  };

  // Check if a mapping's target is used by another column (duplicate target)
  const isDuplicateTarget = (mapping: ColumnMapping) => {
    if (!mapping.targetField) return false;
    return activeMapping.some(
      m => m.sourceColumn !== mapping.sourceColumn && m.targetField === mapping.targetField,
    );
  };

  const handleSaveMapping = async () => {
    if (!analysisId) return;

    try {
      await bomIntelligenceService.updateColumnMapping(analysisId, columnMapping);
      await loadPreview(); // Reload with new mapping
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update mapping');
    }
  };

  // ──────────────── Slice -1: per-line preview edits ────────────────
  // Each handler optimistically mutates local `preview` so the UI feels
  // instant, then fires the API call. On failure we rollback by reloading.

  const patchRow = useCallback(
    (lineItemId: string, patch: Partial<ParsedRow>) => {
      setPreview((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          rows: prev.rows.map((r) =>
            r.lineItemId === lineItemId ? { ...r, ...patch } : r,
          ),
        };
      });
    },
    [],
  );

  const setRowBusy = useCallback((lineItemId: string, busy: boolean) => {
    setBusyRows((prev) => {
      const next = new Set(prev);
      if (busy) next.add(lineItemId);
      else next.delete(lineItemId);
      return next;
    });
  }, []);

  const handleClassificationChange = useCallback(
    async (lineItemId: string, value: PartClassificationCode | '') => {
      if (!analysisId) return;
      const newValue = value || null;
      // Optimistic
      patchRow(lineItemId, { partClassificationOverride: newValue ?? undefined });
      setRowBusy(lineItemId, true);
      try {
        await bomIntelligenceService.setPreviewLineClassification(
          analysisId,
          lineItemId,
          newValue,
        );
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to update part type');
        // Rollback by refetching
        await loadPreview();
      } finally {
        setRowBusy(lineItemId, false);
      }
    },
    [analysisId, patchRow, setRowBusy, loadPreview],
  );

  // Per-line sourcing policy, declared on the SAME screen as the BOM policy.
  // Stored on the analysis row and carried onto the line at confirm, so the
  // exception survives without the BOM having to exist first.
  const handleConstraintsChange = useCallback(
    async (
      lineItemId: string,
      mode: ConstraintMode,
      constraints?: SourcingConstraints,
    ) => {
      if (!analysisId) return;
      patchRow(lineItemId, { constraintMode: mode, sourcingConstraints: constraints });
      setRowBusy(lineItemId, true);
      try {
        await bomIntelligenceService.setPreviewLineConstraints(
          analysisId,
          lineItemId,
          mode,
          constraints,
        );
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to update the line sourcing policy');
        await loadPreview();
      } finally {
        setRowBusy(lineItemId, false);
      }
    },
    [analysisId, patchRow, setRowBusy, loadPreview],
  );

  // Goals are saved per cell, in the background: the row stays editable, and a
  // failed save reloads the preview so the screen never shows an unsaved value.
  const handleGoalsChange = useCallback(
    async (lineItemId: string, goals: LineGoals) => {
      if (!analysisId) return;
      setPreview((prev) =>
        prev
          ? {
              ...prev,
              rows: prev.rows.map((r) =>
                r.lineItemId === lineItemId ? { ...r, goals: { ...r.goals, ...goals } } : r,
              ),
            }
          : prev,
      );
      try {
        await bomIntelligenceService.setPreviewLineGoals(analysisId, lineItemId, goals);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to save the line goal');
        await loadPreview();
      }
    },
    [analysisId, loadPreview],
  );

  // A changed cell is read through the column mapping again on the server and
  // the line is checked again: a fixed line can be imported. A failed save
  // reloads the preview so the screen never shows an unsaved value.
  const handleCellsChange = useCallback(
    async (lineItemId: string, cells: Record<string, string>): Promise<ParsedRow> => {
      if (!analysisId) throw new Error('No analysis');
      try {
        const res = await bomIntelligenceService.setPreviewLineCells(analysisId, lineItemId, cells);
        let updated: ParsedRow | undefined;
        setPreview((prev) =>
          prev
            ? {
                ...prev,
                validRows: res.validRows,
                errorRows: res.errorRows,
                rows: prev.rows.map((r) => {
                  if (r.lineItemId !== lineItemId) return r;
                  updated = { ...r, ...res.row };
                  return updated;
                }),
              }
            : prev,
        );
        return updated ?? ({ ...(preview?.rows.find((r) => r.lineItemId === lineItemId) as ParsedRow), ...res.row });
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to save the change to the line');
        await loadPreview();
        throw err;
      }
    },
    [analysisId, loadPreview, preview],
  );

  const handleLineAlternativesChange = useCallback(
    async (lineItemId: string, value: AlternativesSearchChoice) => {
      if (!analysisId) return;
      patchRow(lineItemId, { alternativesSearch: value ?? undefined });
      try {
        await bomIntelligenceService.setPreviewLineAlternativesSearch(analysisId, lineItemId, value);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to save the alternatives choice for the line');
        await loadPreview();
        throw err;
      }
    },
    [analysisId, patchRow, loadPreview],
  );

  const handleLineComplianceChange = useCallback(
    async (lineItemId: string, value: ComplianceCheckChoice) => {
      if (!analysisId) return;
      patchRow(lineItemId, { complianceCheck: value ?? undefined });
      try {
        await bomIntelligenceService.setPreviewLineComplianceCheck(analysisId, lineItemId, value);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to save the compliance choice for the line');
        await loadPreview();
        throw err;
      }
    },
    [analysisId, patchRow, loadPreview],
  );

  const handleSourcingModeToggle = useCallback(
    async (lineItemId: string, includeDiscovery: boolean) => {
      if (!analysisId) return;
      const newValue: SourcingModeCode = includeDiscovery ? 'INCLUDE_DISCOVERY' : 'AVL_ONLY';
      patchRow(lineItemId, { sourcingMode: newValue });
      setRowBusy(lineItemId, true);
      try {
        await bomIntelligenceService.setPreviewLineSourcingMode(
          analysisId,
          lineItemId,
          newValue,
        );
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to update discovery mode');
        await loadPreview();
      } finally {
        setRowBusy(lineItemId, false);
      }
    },
    [analysisId, patchRow, setRowBusy, loadPreview],
  );

  const handleDocumentUpload = useCallback(
    async (lineItemId: string, file: File, type: DocType) => {
      if (!analysisId) return;
      setRowBusy(lineItemId, true);
      try {
        const result = await bomIntelligenceService.uploadPreviewLineDocument(
          analysisId,
          lineItemId,
          file,
          type,
        );
        // Server appended to pendingDocuments[]. Mirror the same in local
        // state. DRAWING also flips classification server-side; mirror.
        setPreview((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            rows: prev.rows.map((r) => {
              if (r.lineItemId !== lineItemId) return r;
              return {
                ...r,
                pendingDocuments: [...(r.pendingDocuments ?? []), result.pendingDocument],
                ...(type === 'DRAWING' ? { partClassificationOverride: 'CUSTOM_MACHINED' as const } : {}),
              };
            }),
          };
        });
      } catch (err: any) {
        setError(err.response?.data?.message || `${type} upload failed for line ${lineItemId}`);
      } finally {
        setRowBusy(lineItemId, false);
      }
    },
    [analysisId, setRowBusy],
  );

  const handleDocumentDetach = useCallback(
    async (lineItemId: string, documentId: string) => {
      if (!analysisId) return;
      // Optimistic: drop the doc from the local row state.
      setPreview((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          rows: prev.rows.map((r) => {
            if (r.lineItemId !== lineItemId) return r;
            return {
              ...r,
              pendingDocuments: (r.pendingDocuments ?? []).filter((d) => d.documentId !== documentId),
            };
          }),
        };
      });
      setRowBusy(lineItemId, true);
      try {
        await bomIntelligenceService.detachPreviewLineDocument(analysisId, lineItemId, documentId);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to detach document');
        await loadPreview();
      } finally {
        setRowBusy(lineItemId, false);
      }
    },
    [analysisId, setRowBusy, loadPreview],
  );

  const toggleRowExclusion = (rowIndex: number) => {
    setExcludedRows(prev => {
      const next = new Set(prev);
      if (next.has(rowIndex)) {
        next.delete(rowIndex);
      } else {
        next.add(rowIndex);
      }
      return next;
    });
  };

  const handleCreateBom = async () => {
    if (!analysisId || !bomName.trim()) return;

    setCreating(true);
    try {
      // Strips blank chips and empty rule rows; returns undefined when the
      // buyer left the policy alone, so we never persist an empty object that
      // later reads as "a policy was set".
      const sourcingConstraints = cleanConstraints(sourcingPolicy);

      const result = await bomIntelligenceService.confirmAndCreateBom(
        analysisId,
        bomName,
        {
          excludeRows: Array.from(excludedRows),
          workflowId,
          partPool,
          concurrency,
          localPnPrefixOverride: pnPrefixOverride.trim() || undefined,
          forceAutoGeneratePartNumbers: forceAutoGenPn,
          skipExistingPartReuse: !reuseExistingParts,
          sourcingConstraints,
          processing,
          alternativesSearch: bomAlternatives === 'OFF' ? 'OFF' : undefined,
          complianceCheck: bomCompliance === 'ON' ? 'ON' : undefined,
          complianceRegulations: bomCompliance === 'ON' && bomRegulations.length ? bomRegulations : undefined,
        },
      );

      // Where to look for offers, set on the new BOM before its search starts below.
      if (bomOfferMode) {
        await externalDatabasesService.setBomMode(result.bomId, bomOfferMode).catch(() => undefined);
      }

      // Kick off enrichment in the background, then land directly on the BOM's
      // Baseline tab — skip the intermediate Intelligence dashboard. The
      // dashboard stays reachable via the Intelligence icon on the BOM header.
      bomIntelligenceService.enrichAll(result.bomId).catch(() => {
        /* fire-and-forget — the pipeline status strip on the BOM page surfaces failures */
      });
      navigate(`/bom/${result.bomId}`, {
        state: { selectBaseline: true },
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create BOM');
    } finally {
      setCreating(false);
    }
  };

  const getConfidenceBadge = (confidence: number) => {
    if (confidence >= 0.9) {
      return (
        <Chip
          icon={<ValidIcon sx={{ fontSize: 14 }} />}
          label={`${Math.round(confidence * 100)}%`}
          size="small"
          sx={{
            bgcolor: alpha('#2ECC71', 0.1),
            color: '#2ECC71',
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 24,
          }}
        />
      );
    }
    if (confidence >= 0.6) {
      return (
        <Chip
          icon={<WarningIcon sx={{ fontSize: 14 }} />}
          label={`${Math.round(confidence * 100)}%`}
          size="small"
          sx={{
            bgcolor: alpha(semanticColors.confidence.medium, 0.1),
            color: semanticColors.confidence.medium,
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 24,
          }}
        />
      );
    }
    return (
      <Chip
        icon={<ErrorIcon sx={{ fontSize: 14 }} />}
        label={`${Math.round(confidence * 100)}%`}
        size="small"
        sx={{
          bgcolor: alpha('#E74C3C', 0.1),
          color: '#E74C3C',
          fontWeight: 600,
          fontSize: '0.7rem',
          height: 24,
        }}
      />
    );
  };

  const getRowStatusIcon = (row: ParsedRow) => {
    if (row.errors && row.errors.length > 0) {
      return (
        <Tooltip title={row.errors.join(', ')}>
          <ErrorIcon sx={{ fontSize: 18, color: '#E74C3C' }} />
        </Tooltip>
      );
    }
    if (row.warnings && row.warnings.length > 0) {
      return (
        <Tooltip title={row.warnings.join(', ')}>
          <WarningIcon sx={{ fontSize: 18, color: semanticColors.confidence.medium }} />
        </Tooltip>
      );
    }
    return <ValidIcon sx={{ fontSize: 18, color: '#2ECC71' }} />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: semanticColors.accent.main }} />
      </Box>
    );
  }

  // If this is a CAD file, render the CAD-specific preview
  if (preview && (preview.fileType === 'CAD_STEP' || preview.fileType === 'CAD_IGES')) {
    return <CadPreviewPage preview={preview} onRefresh={loadPreview} />;
  }

  if (error && !preview) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', py: 4 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/bom-intelligence/upload')}>
          Back to Upload
        </Button>
      </Box>
    );
  }

  const validRows = preview?.rows?.filter(r => r.isValid && !excludedRows.has(r.rowIndex)) || [];
  const mappedColumns = columnMapping.filter(m => m.targetField);

  const activeRows = preview?.rows || [];
  const activeMapping = columnMapping;
  const activeMappedColumns = mappedColumns;
  const activeTotalRows = preview?.totalRows || 0;
  const activeTargetFields = TARGET_FIELDS;

  // Part Number source: which mapping field carries the local PN, how many
  // rows have a value, and how many would auto-generate at commit.
  const localIdField = 'partNumber';
  const mappedPnSourceColumn = activeMapping.find(
    (m) => m.targetField === localIdField,
  )?.sourceColumn;
  // DOCUMENT lines pin a registry document instead of a Part, so they never
  // take a part number. Counting them as "needs a PN" is what produced the
  // phantom "3 auto-generated" chip on customer packages.
  const pnBearingRows = validRows.filter((r) => (r as any).lineType !== 'DOCUMENT');
  const rowsWithMappedPn = pnBearingRows.filter((r) => String(r.mappedData?.[localIdField] ?? '').trim() !== '').length;
  const rowsMissingPn = pnBearingRows.length - rowsWithMappedPn;
  // When `forceAutoGenPn` is on, every PN-bearing row gets generated;
  // otherwise only the missing ones do.
  const autoGenRowCount = forceAutoGenPn ? pnBearingRows.length : rowsMissingPn;
  const mappedKeptCount = !forceAutoGenPn ? rowsWithMappedPn : 0;

  const activeErrorRows = preview?.errorRows || 0;
  const nameTooShort = bomName.trim().length < 3;
  const bomPolicySet = !!cleanConstraints(sourcingPolicy) || bomAlternatives === 'OFF';
  // Neither a part number nor a description column: the lines would import with no identity.
  const noIdentityColumn =
    preview?.importMode !== 'package' &&
    columnMapping.length > 0 &&
    !columnMapping.some((m) => ['partNumber', 'mpn', 'partDescription', 'localCode'].includes(m.targetField));

  // Every import has all its settings on this page and is created directly.
  const handleCreateClick = () => {
    void handleCreateBom();
  };
  // The Portfolio page's look (tokens in PortfolioParts): ink for what is
  // chosen and for the primary action, small upper-case captions, hairlines.
  const stripLabel = { fontSize: 11, fontWeight: 600, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.07em', mb: 1 } as const;
  const stripHint = { fontSize: 12.5, color: T.muted, mt: 1 } as const;

  return (
    <ThemeProvider theme={withIbmPlex}>
    <Box sx={{ maxWidth: 1400, mx: 'auto', py: 3, fontFamily: IBM_PLEX_SANS }}>
      {/* Header: the BOM's name, its sourcing policy, and Create. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/bom-intelligence/upload')} aria-label="Back to import">
          <BackIcon />
        </IconButton>
        <TextField
          variant="standard"
          value={bomName}
          onChange={(e) => setBomName(e.target.value)}
          placeholder="BOM name"
          error={nameTooShort}
          helperText={nameTooShort ? 'Name the BOM, at least 3 characters' : undefined}
          inputProps={{ maxLength: 120, 'aria-label': 'BOM name' }}
          InputProps={{ disableUnderline: !nameTooShort }}
          sx={{
            flex: '1 1 280px',
            maxWidth: 560,
            '& input': { fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', color: T.ink },
          }}
        />
        <Box sx={{ flex: 1 }} />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            pl: 0.75,
            pr: 1.25,
            py: 0.5,
            border: '1px solid',
            borderColor: bomPolicySet ? T.navy : T.line,
            bgcolor: '#fff',
            borderRadius: 999,
            '& .MuiIconButton-root': { opacity: 1, color: T.navy },
          }}
        >
          {/* The same policy control as the BOM page, the part page and each line. */}
          <SourcingPolicyControl
            scope="bom"
            prominent
            label={bomName || 'This BOM'}
            resolved={effectiveForLine(
              undefined,
              cleanConstraints(sourcingPolicy)
                ? { constraintMode: 'OVERRIDE', sourcingConstraints: sourcingPolicy }
                : {},
            )}
            onSave={(mode, constraints) =>
              setSourcingPolicy(mode === 'OVERRIDE' && constraints ? constraints : EMPTY_CONSTRAINTS)
            }
            alternativesSearch={bomAlternatives}
            onAlternativesSearchChange={setBomAlternatives}
            complianceCheck={bomCompliance}
            onComplianceCheckChange={setBomCompliance}
            complianceRegulations={bomRegulations}
            onComplianceRegulationsChange={setBomRegulations}
            offerSearchMode={bomOfferMode}
            onOfferSearchModeChange={setBomOfferMode}
          />
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.navy }}>
            {bomAlternatives === 'OFF'
              ? cleanConstraints(sourcingPolicy)
                ? 'Policy set · no alternatives'
                : 'No alternatives'
              : cleanConstraints(sourcingPolicy)
                ? 'Policy set'
                : 'Sourcing policy'}
            {bomCompliance === 'ON' ? ' · compliance' : ''}
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={loadPreview} aria-label="Refresh">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <Button
          variant="contained"
          startIcon={creating ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <SaveIcon />}
          onClick={handleCreateClick}
          disabled={validRows.length === 0 || creating || nameTooShort}
          disableElevation
          sx={{
            bgcolor: T.navy,
            textTransform: 'none',
            fontWeight: 600,
            letterSpacing: '0.01em',
            borderRadius: 999,
            px: 2.75,
            boxShadow: `0 2px 8px ${alpha(T.navy, 0.25)}`,
            '&:hover': { bgcolor: alpha(T.navy, 0.92), boxShadow: `0 4px 14px ${alpha(T.navy, 0.32)}` },
          }}
        >
          {creating ? 'Creating…' : `Create BOM (${validRows.length} items)`}
        </Button>
      </Box>

      {/* One quiet line where four count tiles used to be. Errors speak up only when there are some. */}
      <Typography sx={{ fontSize: 13.5, color: T.muted, mb: 2.5, ml: 6.5 }}>
        {preview?.originalFilename ? `${preview.originalFilename} · ` : ''}
        {activeTotalRows} rows
        {activeErrorRows > 0 && (
          <Box component="span" sx={{ color: T.red, fontWeight: 600 }}>
            {' '}· {activeErrorRows} with errors
          </Box>
        )}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* A file whose identity columns were not recognised: the one case where the mapping asks for attention. */}
      {noIdentityColumn && (
        <Alert
          severity="warning"
          sx={{ mb: 2, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setAdvancedOpen(true)} sx={{ textTransform: 'none' }}>
              Open column mapping
            </Button>
          }
        >
          No part number or description column was recognised in this file.
        </Alert>
      )}

      {/* Setup strip: the choices every import makes, set in place. */}
      <Paper
        elevation={0}
        sx={{
          mb: 1,
          border: `1px solid ${T.line}`,
          borderRadius: 3,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
          '& > div': { p: 2, minWidth: 0 },
          '& > div + div': { borderLeft: { md: '1px solid #e2e8f0' }, borderTop: { xs: '1px solid #e2e8f0', md: 'none' } },
        }}
      >
        <Box>
          <Typography sx={stripLabel}>Processing</Typography>
          <SegmentedChoice
            ariaLabel="Processing"
            value={processing}
            onChange={setProcessing}
            options={PROCESSING_LEVELS.map((l) => ({ value: l.value, label: l.label }))}
          />
          <Typography sx={stripHint}>
            {processing !== 'FULL'
              ? `${processingLevel(processing).describe} · ${processingLevel(processing).flow}`
              : `${processingLevel('FULL').describe} · ${workflowId}`}
            {bomAlternatives === 'OFF' && processing !== 'LEAN' ? ' · no alternatives, by policy' : ''}
          </Typography>
        </Box>
        <Box>
          <Typography sx={stripLabel}>Part numbers</Typography>
          <SegmentedChoice
            ariaLabel="Part numbers"
            value={forceAutoGenPn ? 'new' : 'file'}
            onChange={(v) => setForceAutoGenPn(v === 'new')}
            options={[
              { value: 'file', label: 'From the file' },
              { value: 'new', label: 'Generate new' },
            ]}
          />
          <Typography sx={stripHint}>
            {forceAutoGenPn
              ? `New numbers for all ${autoGenRowCount} lines`
              : rowsWithMappedPn === 0
                ? `No part number in the file: ${rowsMissingPn} generated`
                : rowsMissingPn > 0
                  ? `${rowsWithMappedPn} from the file, ${rowsMissingPn} generated`
                  : `${mappedPnSourceColumn ? `Column "${mappedPnSourceColumn}", ` : ''}all ${rowsWithMappedPn} lines have one`}
            {autoGenRowCount > 0 && pnPreview?.enabled && pnPreview.prefix ? ` (${pnPreview.prefix})` : ''}
          </Typography>
          {autoGenRowCount > 0 &&
            (pnPreview?.enabled && pnPreview.prefix ? (
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
                {pnPreview.samples.length > 0 && (
                  <Typography sx={{ fontSize: 12.5, color: T.muted }}>
                    Next: <Box component="span" sx={{ fontFamily: T.mono, color: T.ink2 }}>{pnPreview.samples.join(', ')}</Box>
                  </Typography>
                )}
                <TextField
                  variant="standard"
                  placeholder={`Prefix for this import (${pnPreview.prefix})`}
                  value={pnPrefixOverride}
                  onChange={(e) => setPnPrefixOverride(e.target.value)}
                  inputProps={{ 'aria-label': 'Override prefix for this import', style: { fontSize: 12.5 } }}
                  sx={{ minWidth: 220 }}
                />
              </Stack>
            ) : (
              <Box sx={{ mt: 1 }}>
                <Typography sx={{ fontSize: 12.5, color: T.red, mb: 0.5 }}>
                  No part number prefix is set for this tenant (Domain Config, Part Numbering). Type one for this import:
                </Typography>
                <TextField
                  variant="standard"
                  placeholder="e.g. CPC-"
                  value={pnPrefixOverride}
                  onChange={(e) => setPnPrefixOverride(e.target.value)}
                  inputProps={{ 'aria-label': 'One-off prefix', style: { fontSize: 12.5 } }}
                  sx={{ minWidth: 220 }}
                />
              </Box>
            ))}
        </Box>
        <Box>
          <Typography sx={stripLabel}>Earlier results</Typography>
          <SegmentedChoice
            ariaLabel="Earlier results"
            value={reuseExistingParts ? 'reuse' : 'fresh'}
            onChange={(v) => setReuseExistingParts(v === 'reuse')}
            options={[
              { value: 'reuse', label: 'Reuse' },
              { value: 'fresh', label: 'Search again' },
            ]}
          />
          <Typography sx={stripHint}>
            {reuseExistingParts
              ? 'Existing parts, known sellers and known alternatives are used. Only what is missing is searched.'
              : 'Nothing from earlier runs is used. Parts, sellers and alternatives are all searched again.'}
          </Typography>
        </Box>
        <Box>
          <Typography sx={stripLabel}>Compliance</Typography>
          <SegmentedChoice
            ariaLabel="Compliance check"
            value={bomCompliance === 'ON' ? 'ON' : 'OFF'}
            onChange={(v) => setBomCompliance(v === 'ON' ? 'ON' : null)}
            options={[
              { value: 'OFF', label: 'Off' },
              { value: 'ON', label: 'Check', tooltip: "Finds each maker's own POPs, RoHS, REACH and PFAS statements, with the document. Uses web search." },
            ]}
          />
          {bomCompliance === 'ON' ? (
            <Stack direction="row" sx={{ mt: 1, flexWrap: 'wrap', gap: 0.75 }}>
              {COMPLIANCE_REGULATION_OPTIONS.map((r) => {
                const chosen = bomRegulations.length ? bomRegulations : DEFAULT_COMPLIANCE_REGULATIONS;
                const on = chosen.includes(r.key);
                return (
                  <Chip
                    key={r.key}
                    label={r.label}
                    size="small"
                    variant={on ? 'filled' : 'outlined'}
                    onClick={() => {
                      const next = on ? chosen.filter((k) => k !== r.key) : [...chosen, r.key];
                      if (next.length) setBomRegulations(next);
                    }}
                    sx={{ fontWeight: 600, ...(on ? { bgcolor: T.navy, color: '#fff' } : { color: T.muted }) }}
                  />
                );
              })}
            </Stack>
          ) : (
            <Typography sx={stripHint}>Not checked. Uses web search when on.</Typography>
          )}
        </Box>
      </Paper>

      {/* Advanced settings: part number details, column mapping, part pool, concurrency. */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <Button
          size="small"
          onClick={() => setAdvancedOpen((open) => !open)}
          endIcon={<ExpandMoreIcon sx={{ transform: advancedOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />}
          sx={{ textTransform: 'none', color: T.ink, fontWeight: 600 }}
        >
          Advanced settings
        </Button>
      </Box>
      <Collapse in={advancedOpen} unmountOnExit>
      {/* Column Mapping Section.
          Customer-package imports are mapped by their ingestion PROFILE, not
          by a user-editable column mapping — show what happened instead of an
          empty editor with a button the backend would reject. */}
      {preview?.importMode === 'package' ? (
        <Paper elevation={0} sx={{ px: 2.5, py: 2, mb: 2, border: `1px solid ${T.line}`, borderRadius: 3 }}>
          <Typography sx={stripLabel}>Column mapping</Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            This is a customer package import: rows were mapped by the ingestion
            profile (columns, folder structure, revisions, document links), so
            there is no column mapping to edit here. Part numbers, revisions and
            attached documents come from the package itself. To change how this
            customer's packages are read, update the ingestion profile.
          </Typography>
        </Paper>
      ) : (
      <Paper elevation={0} sx={{ mb: 2, border: `1px solid ${T.line}`, borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${T.line}` }}>
          <Typography sx={stripLabel} style={{ marginBottom: 0 }}>Column mapping</Typography>
          {/* Columns read by AI (unknown header words) or left unmapped: a quiet (i), not a banner. */}
          {(columnMapping.some((m: any) => m.aiSuggested) || columnMapping.some((m) => !m.targetField)) && (
            <Tooltip
              title={[
                columnMapping.filter((m: any) => m.aiSuggested).length
                  ? `Read by AI (confirm or change): ${columnMapping.filter((m: any) => m.aiSuggested).map((m) => m.sourceColumn).join(', ')}`
                  : '',
                columnMapping.filter((m) => !m.targetField).length
                  ? `Not imported (no mapping): ${columnMapping.filter((m) => !m.targetField).map((m) => m.sourceColumn).join(', ')}`
                  : '',
              ].filter(Boolean).join(' · ')}
            >
              <InfoOutlinedIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
            </Tooltip>
          )}
          <Typography sx={{ fontSize: 12.5, color: T.muted }}>
            {activeMappedColumns.length} of {activeMapping.length} columns used
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="outlined"
            onClick={handleSaveMapping}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 999, px: 2, color: T.navy, borderColor: T.line, '&:hover': { borderColor: T.navy } }}
          >
            Apply mapping
          </Button>
        </Box>
        {/* One aligned row per file column: its name, the field it fills, and a note only when it needs a look. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, columnGap: 4, px: 2.5, py: 0.5 }}>
          {activeMapping.map((mapping) => {
            const unsure = !!mapping.targetField && (mapping.confidence ?? 0) < 0.9;
            return (
              <Box
                key={mapping.sourceColumn}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) 16px minmax(0, 220px) 96px',
                  alignItems: 'center',
                  columnGap: 1.5,
                  py: 0.75,
                  borderBottom: `1px solid ${T.soft}`,
                }}
              >
                <Typography noWrap dir="auto" title={mapping.sourceColumn} sx={{ fontSize: 13.5, fontWeight: 600, color: mapping.targetField ? T.ink : T.faint }}>
                  {mapping.sourceColumn}
                </Typography>
                <Typography sx={{ fontSize: 13, color: T.faint }}>→</Typography>
                <Select
                  variant="standard"
                  disableUnderline
                  value={mapping.targetField}
                  onChange={(e) => handleMappingChange(mapping.sourceColumn, e.target.value)}
                  displayEmpty
                  inputProps={{ 'aria-label': `Field for ${mapping.sourceColumn}` }}
                  sx={{
                    fontSize: 13.5,
                    color: mapping.targetField ? T.ink : T.faint,
                    bgcolor: T.bg,
                    borderRadius: 2,
                    '& .MuiSelect-select': { py: 0.6, px: 1.25 },
                    '&:hover': { bgcolor: T.soft },
                  }}
                >
                  {activeTargetFields.map(field => (
                    <MenuItem key={field.value} value={field.value} sx={{ fontSize: 13.5 }}>
                      {field.label}
                    </MenuItem>
                  ))}
                </Select>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                  {unsure && (
                    <Tooltip title="Matched with low confidence. Check that this is the right field.">
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.amber, fontFamily: T.mono }}>
                        Check {Math.round((mapping.confidence ?? 0) * 100)}%
                      </Typography>
                    </Tooltip>
                  )}
                  {isDuplicateTarget(mapping) && (
                    <Tooltip title="Concatenate with other columns mapped to the same field (joined with ' | ')">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                          size="small"
                          checked={!!mapping.concatenate}
                          onChange={() => handleConcatenateToggle(mapping.sourceColumn)}
                          sx={{ p: 0.25, '&.Mui-checked': { color: T.navy } }}
                        />
                        <Typography sx={{ fontSize: 11, color: T.muted }}>Concat</Typography>
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Paper>
      )}

      <Paper elevation={0} sx={{ mb: 3, px: 2.5, py: 2, border: `1px solid ${T.line}`, borderRadius: 3 }}>
        <Typography sx={stripLabel}>Enrichment</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'flex-start' }}>
          <Box>
            <Typography sx={{ fontSize: 12.5, color: T.muted, mb: 0.5 }}>Part pool</Typography>
            <Tooltip title="Where to look for matching parts. Gold = curated reference catalog; Central = cross-tenant; Local = this tenant only." placement="top" arrow>
              <Box>
                <SegmentedChoice
                  ariaLabel="Part pool"
                  value={partPool}
                  onChange={setPartPool}
                  options={[
                    { value: 'local', label: 'Local' },
                    { value: 'gold', label: 'Gold' },
                    { value: 'central', label: 'Central' },
                  ]}
                />
              </Box>
            </Tooltip>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12.5, color: T.muted, mb: 0.5 }}>Parts processed at once (1 to 50)</Typography>
            {/* Same height and rounding as the sliding control beside it, so the two sit on one line. */}
            <TextField
              variant="outlined"
              size="small"
              type="number"
              value={concurrency}
              onChange={(e) => setConcurrency(Math.max(1, Math.min(50, Number(e.target.value) || 10)))}
              inputProps={{ min: 1, max: 50, 'aria-label': 'Concurrency', style: { fontFamily: T.mono, fontSize: 14, textAlign: 'center', padding: '0 12px' } }}
              sx={{
                width: 96,
                '& .MuiOutlinedInput-root': { height: 38, borderRadius: 999, bgcolor: '#fff' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: T.line },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.faint },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.navy, borderWidth: 1 },
              }}
            />
          </Box>
        </Stack>
      </Paper>
      </Collapse>

      {/* Data Preview Table */}
      <Paper elevation={0} sx={{ border: `1px solid ${T.line}`, borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${T.line}`, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: T.ink }}>
            Lines
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: T.muted }}>
            {validRows.length} will be imported
          </Typography>
          {/* Currency the prices are actually in. Most BOMs state it once, in
              the price column header, and never per row — so without showing it
              here the buyer confirms an import with no idea whether the numbers
              are dollars. */}
          {/* Which sheet was read, and the prices' currency, are in the subtitle and the price column. */}
          {/* A multi-configuration ("150%") BOM: its variant switch sits in the table's toolbar. */}
        </Box>

        <BomPreviewDataGrid
          rows={activeRows}
          mappedColumns={activeMappedColumns}
          targetFields={activeTargetFields}
          excludedRows={excludedRows}
          toggleRowExclusion={toggleRowExclusion}
          getRowStatusIcon={getRowStatusIcon}
          getConfidenceBadge={getConfidenceBadge}
          busyRows={busyRows}
          classificationOptions={PART_CLASSIFICATION_OPTIONS}
          documentChipRenderer={({ row, busy, onUpload, onDetach }) => (
            <DocumentChip
              documents={row.pendingDocuments ?? []}
              requirements={row.requirements}
              busy={busy}
              onUpload={onUpload}
              onDetach={onDetach}
            />
          )}
          onClassificationChange={handleClassificationChange}
          onSourcingModeToggle={handleSourcingModeToggle}
          bomPolicy={sourcingPolicy}
          onConstraintsChange={handleConstraintsChange}
          onGoalsChange={handleGoalsChange}
          bomAlternativesSearch={bomAlternatives}
          onAlternativesSearchChange={handleLineAlternativesChange}
          bomComplianceCheck={bomCompliance}
          onComplianceCheckChange={handleLineComplianceChange}
          onDocumentUpload={handleDocumentUpload}
          onDocumentDetach={handleDocumentDetach}
          configurations={preview?.configurations}
          currency={preview?.importCurrency}
          onCellsChange={preview?.importMode !== 'package' && columnMapping.length > 0 ? handleCellsChange : undefined}
        />
      </Paper>

    </Box>
    </ThemeProvider>
  );
};

export default BomPreviewPage;
